import React from 'react';
import './ThreePlyReflectivePage.css';

const comparisonRows = [
  {
    feature: 'Layers',
    single: '1 Layer',
    double: '2 Layers',
    triple: '3 Advanced Layers',
  },
  {
    feature: 'Heat Rejection',
    single: 'Low to Moderate',
    double: 'Moderate',
    triple: 'High – Reflective & Absorptive Layers Work Together',
  },
  {
    feature: 'Glare Reduction',
    single: 'Basic',
    double: 'Good',
    triple: 'Excellent – Optimized for Comfort & Visibility',
  },
  {
    feature: 'UV Protection',
    single: 'Up to 100%',
    double: 'Up to 100%',
    triple: 'Up to 100% – Ideal for Interior Protection & Skin Safety',
  },
  {
    feature: 'Durability',
    single: 'Basic – Prone to Peeling & Scratches',
    double: 'Better than single ply',
    triple: 'High Durability – Scratch Resistant & Weather Proof',
  },
  {
    feature: 'Aesthetic Finish',
    single: 'Slight Tint',
    double: 'Neutral Appearance',
    triple: 'Sleek Reflective Exterior / Charcoal Interior Finish',
  },
  {
    feature: 'Privacy',
    single: 'Minimal Privacy',
    double: 'Moderate Privacy',
    triple: 'Daytime Privacy – One-Way Reflective Appearance',
  },
  {
    feature: 'Cost',
    single: 'Economical',
    double: 'Mid-Range',
    triple: 'Premium – Long-Term Investment with High Performance',
  },
  {
    feature: 'Best For',
    single: 'Short-Term Use, Budget Applications',
    double: 'Standard Homes & Offices',
    triple: 'High-End Homes, Corporate Buildings, Sun-Exposed Areas',
  },
];

const models = [
  { name: 'DP DGR R 15', VLT: '11%', IRR: '74%', UVR: '100%', OD: '0.98', TSER: '70%' },
  { name: 'DP BLBK R 15', VLT: '1%', IRR: '91%', UVR: '100%', OD: '1.5', TSER: '84%' },
  { name: 'DP GRBL R 15', VLT: '2%', IRR: '88%', UVR: '100%', OD: '1.81', TSER: '84%' },
  { name: 'DP GRGN R 10', VLT: '5%', IRR: '88%', UVR: '100%', OD: '1.28', TSER: '77%' },
  { name: 'DP BL R 10', VLT: '10%', IRR: '95%', UVR: '100%', OD: '1.05', TSER: '78%' },
];

const ThreePlyReflectivePage = () => (
  <main className="three-ply-main">
    <section className="three-ply-hero">
      <h1>3 ply reflective film</h1>
    </section>

    <section className="three-ply-intro-section">
      <h2 className="three-ply-subtitle">Triple-Layered Protection for Comfort, Efficiency & Style</h2>
      <p className="three-ply-intro">Derma Protec's 3 Ply Reflective Solar Tinted Film represents the latest in high-performance window film technology. Built with three specialized layers, it offers superior solar control, enhanced durability, and a refined aesthetic—making it an ideal solution for homes, offices, and commercial buildings.</p>
    </section>

    <section className="three-ply-tech">
      <h2>🔍 What is "3 Ply" Technology?</h2>
      <div className="three-ply-layers">
        <div className="three-ply-layer">
          <span className="three-ply-icon">🛡️</span>
          <h3>1. Outer Reflective Layer</h3>
          <ul>
            <li>Crafted with a metallic finish, this layer reflects a large portion of solar radiation and visible light.</li>
            <li>Provides a mirror-like effect when viewed from the outside, delivering daytime privacy and reducing incoming heat.</li>
          </ul>
        </div>
        <div className="three-ply-layer">
          <span className="three-ply-icon">🔥</span>
          <h3>2. Middle Absorptive Layer</h3>
          <ul>
            <li>This layer absorbs residual heat and disperses it evenly across the film.</li>
            <li>Helps prevent hotspots and keeps indoor temperatures balanced, minimizing the strain on cooling systems.</li>
          </ul>
        </div>
        <div className="three-ply-layer">
          <span className="three-ply-icon">🌞</span>
          <h3>3. Inner UV-Blocking Layer</h3>
          <ul>
            <li>Blocks up to 100% of harmful UV rays that cause fading to furniture, floors, and interiors.</li>
            <li>Adds structural strength, improves adhesion to glass, and increases scratch resistance.</li>
          </ul>
        </div>
      </div>
    </section>
    <div className="three-ply-img-center">
      <img src="https://i.postimg.cc/tCYszbDf/3-ply-refl-derma.png" alt="3 Ply Reflective Derma Example" className="three-ply-content-img" />
    </div>

    <section className="three-ply-benefits">
      <h2>🌟 Key Benefits of 3 Ply Reflective Films</h2>
      <ul className="three-ply-benefits-list">
        <li><span role="img" aria-label="heat">🌡️</span> <b>Superior Heat Rejection</b> — Keeps interiors cooler by blocking both infrared and visible solar heat, helping reduce electricity bills.</li>
        <li><span role="img" aria-label="privacy">👁️‍🗨️</span> <b>Daytime Privacy & Glare Reduction</b> — The reflective surface provides enhanced privacy during daylight hours and reduces glare, making it easier to work or relax indoors.</li>
        <li><span role="img" aria-label="uv">🧴</span> <b>UV Protection</b> — Helps prevent sun damage to skin, furniture, flooring, and artwork by blocking 100% of UV rays.</li>
        <li><span role="img" aria-label="weather">💧</span> <b>Weather & Scratch Resistance</b> — Built for durability—resistant to moisture, peeling, and surface abrasions, ensuring longevity in all climates.</li>
        <li><span role="img" aria-label="aesthetics">✨</span> <b>Elegant Aesthetics</b> — Neutral tones from the inside with a sleek reflective look outside—perfect for modernizing facades while staying functional.</li>
      </ul>
    </section>
    <div className="three-ply-img-center">
      <img src="https://i.postimg.cc/9FWpXDm9/3-ply-refle-derma-2.png" alt="3 Ply Reflective Derma Example 2" className="three-ply-content-img" />
    </div>

    <section className="three-ply-why">
      <h2>💡 Why Choose Derma Protec's 3 Ply Films?</h2>
      <p>Unlike single-layer or dyed alternatives, our 3 Ply Films are built to last longer, perform better, and look sharper. With high-grade PET materials and precise engineering, they deliver consistent results—no matter the season.</p>
    </section>

    <section className="three-ply-comparison">
      <h2>✅ Comparison Table: Single Ply vs 2 Ply vs 3 Ply Reflective Films</h2>
      <div className="three-ply-table-wrapper">
        <table className="three-ply-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Single Ply Film</th>
              <th>2 Ply Film</th>
              <th>3 Ply Reflective Film</th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map(row => (
              <tr key={row.feature}>
                <td>{row.feature}</td>
                <td>{row.single}</td>
                <td>{row.double}</td>
                <td>{row.triple}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <section className="three-ply-models">
      <h2>Tri-Ply Reflective Films — Model Specifications</h2>
      <div className="three-ply-table-wrapper">
        <table className="three-ply-table models">
          <thead>
            <tr>
              <th>Model</th>
              <th>VLT</th>
              <th>IRR</th>
              <th>UVR</th>
              <th>O.D</th>
              <th>TSER</th>
            </tr>
          </thead>
          <tbody>
            {models.map(model => (
              <tr key={model.name}>
                <td>{model.name}</td>
                <td>{model.VLT}</td>
                <td>{model.IRR}</td>
                <td>{model.UVR}</td>
                <td>{model.OD}</td>
                <td>{model.TSER}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </main>
);

export default ThreePlyReflectivePage; 